"use client";

import React, { useEffect, useRef } from "react";

interface Benefit {
  number: string;
  title: string;
  body: string;
  stat?: string;
  statLabel?: string;
}

const benefits: Benefit[] = [
  {
    number: "01",
    title: "Plant-Powered Protein",
    body: "With 9g of protein per cookie, it's perfect for post-workout recovery, a mid-day boost, or a smart treat for the whole family.",
    stat: "9g",
    statLabel: "Protein",
  },
  {
    number: "02",
    title: "Fiber for Fullness",
    body: "4g of fiber keeps you satisfied longer and supports healthy digestion — so you snack smarter, not more.",
    stat: "4g",
    statLabel: "Fibre",
  },
  {
    number: "03",
    title: "Gluten-Free Goodness",
    body: "Made with oats and chickpeas, it's a safe and delicious choice for gluten-sensitive snackers.",
  },
  {
    number: "04",
    title: "Vegan Delight",
    body: "No dairy, no eggs — just pure, plant-based love in every bite.",
  },
  {
    number: "05",
    title: "Low-Glycemic Energy",
    body: "A slow, steady release of energy keeps you full longer and your blood sugar balanced.",
  },
];

export default function BenefitsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("is-visible");
        });
      },
      { threshold: 0 }
    );
    sectionRef.current?.querySelectorAll(".reveal-hidden").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="benefits"
      ref={sectionRef}
      className="section-pad relative bg-white"
      suppressHydrationWarning
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="reveal-hidden mb-14">
          <p className="text-xs font-medium uppercase tracking-widest text-pmu-taupe mb-5" style={{ letterSpacing: "0.2em" }}>
            Why Pick Me Up
          </p>
          <h2
            className="font-fraunces font-bold text-pmu-espresso leading-tight max-w-xl"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            Five reasons to love{" "}
            <span className="italic font-light" style={{ color: "#A8C4D8" }}>every bite</span>
          </h2>
        </div>

        {/* Benefits list */}
        <div className="space-y-0">
          {benefits.map((b, i) => (
            <div
              key={b.number}
              className={`reveal-hidden delay-${i} flex flex-col sm:flex-row sm:items-start gap-6 sm:gap-10 py-8 border-t border-pmu-stone/40`}
            >
              {/* Number */}
              <span
                className="font-fraunces font-light text-4xl leading-none shrink-0 w-12"
                style={{ color: "rgba(168,196,216,0.5)" }}
              >
                {b.number}
              </span>

              {/* Content */}
              <div className="flex-1 space-y-2">
                <h3 className="font-fraunces font-semibold text-xl text-pmu-espresso">
                  {b.title}
                </h3>
                <p className="text-base text-pmu-taupe leading-relaxed max-w-lg" style={{ fontWeight: 400 }}>
                  {b.body}
                </p>
              </div>

              {/* Stat */}
              {b.stat && (
                <div className="shrink-0 text-right hidden sm:block">
                  <p className="font-fraunces font-bold text-4xl text-pmu-espresso leading-none">
                    {b.stat}
                  </p>
                  <p className="text-xs text-pmu-taupe uppercase tracking-widest mt-1" style={{ letterSpacing: "0.12em" }}>
                    {b.statLabel}
                  </p>
                </div>
              )}
            </div>
          ))}
          {/* Bottom border */}
          <div className="border-t border-pmu-stone/40" />
        </div>
      </div>
    </section>
  );
}